// 명부 선택 모달(여러 명, 공용) — 알림 파트(2026-10-08, 기획서_환자자동관리_알림파트 §4-1 A-2·§4-2 B-2 (c))
//   검색(이름·끝 4자리) + 체크 + 상한(max). single=true 면 1명만(예약 확인 안내).
//   statusOf(r) → { selectable:boolean, text:string } — 행 오른쪽 사실 표시(설득 문구 없음). selectable=false 행은 체크 비활성.
//   대상은 active 레코드만(삭제·삭제 요청 중 제외). 단골(legacy)은 명부 레코드가 아니라 제외. 동명이인은 배지만(ClinicPatientPicker 와 같은 규칙).
//   내보내기·복사·인쇄 없음. 선택 결과는 부모가 pid 만 서버로 보낸다.
import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import { listClinicPatientRecords, describeError, isAppRegisteredRecord, appRegisterBadgeLabel, sameNameCountMap, sameNameCountOf } from '../../services/clinicPatientsApi';

function ClinicPatientMultiPicker({ clinicId, title = '명부에서 환자 고르기', max = 100, single = false, statusOf, initialSelectedIds = [], hint, onConfirm, onClose }) {
    const [rows, setRows] = useState([]);
    const [nameCounts, setNameCounts] = useState(() => new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [term, setTerm] = useState('');
    const [selected, setSelected] = useState(() => new Set(initialSelectedIds));
    const [limitMsg, setLimitMsg] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await listClinicPatientRecords(clinicId);
                if (alive) { setRows(r.filter(x => x.status === 'active')); setNameCounts(sameNameCountMap(r)); }
            } catch (e) {
                if (alive) setError(describeError(e));
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [clinicId]);

    const statusMap = useMemo(() => {
        const m = new Map();
        for (const r of rows) m.set(r.id, statusOf ? statusOf(r) : { selectable: true, text: '' });
        return m;
    }, [rows, statusOf]);

    const filtered = useMemo(() => {
        const t = term.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter(r => (r.name || '').toLowerCase().includes(t) || (r.phoneLast4 || '').includes(t));
    }, [rows, term]);

    // 상태 갱신은 updater 밖에서 계산(updater 안 부수효과 금지 — StrictMode 이중 호출 대비)
    const toggle = (r) => {
        const st = statusMap.get(r.id);
        if (!st?.selectable) return;
        if (selected.has(r.id)) {
            const next = new Set(selected); next.delete(r.id);
            setSelected(next); setLimitMsg('');
            return;
        }
        if (single) { setSelected(new Set([r.id])); setLimitMsg(''); return; }
        if (selected.size >= max) { setLimitMsg(`한 번에 ${max}명까지 고를 수 있습니다.`); return; }
        const next = new Set(selected); next.add(r.id);
        setSelected(next); setLimitMsg('');
    };
    // 보이는 행 중 고를 수 있는 것 전부 선택(상한까지)
    const selectVisible = () => {
        const next = new Set(selected);
        let capped = false;
        for (const r of filtered) {
            if (!statusMap.get(r.id)?.selectable || next.has(r.id)) continue;
            if (next.size >= max) { capped = true; break; }
            next.add(r.id);
        }
        setSelected(next);
        setLimitMsg(capped ? `한 번에 ${max}명까지 고를 수 있어 ${max}명에서 멈췄습니다.` : '');
    };
    const clearAll = () => { setSelected(new Set()); setLimitMsg(''); };
    const confirm = () => {
        const picked = rows.filter(r => selected.has(r.id));
        if (!picked.length) return;
        onConfirm(picked);
    };

    const disabledCount = useMemo(() => rows.filter(r => !statusMap.get(r.id)?.selectable).length, [rows, statusMap]);

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div role="dialog" aria-modal="true" aria-label={title} style={{ ...U.card, width: '100%', maxWidth: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{title}</h3>
                    <button type="button" aria-label="닫기" onClick={onClose} style={{ ...U.buttonSecondary, padding: '4px 8px' }}><X size={14} /></button>
                </div>
                {hint && <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>{hint}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
                    <Search size={14} color="#94a3b8" />
                    <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="이름 또는 뒤 4자리"
                        style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12.5, color: '#475569' }}>
                    <span>{single ? `선택 ${selected.size}/1명` : `선택 ${selected.size}/${max}명`}</span>
                    {!single && <button type="button" onClick={selectVisible} disabled={loading} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}>{'보이는 환자 전부 고르기'}</button>}
                    {selected.size > 0 && <button type="button" onClick={clearAll} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 }}>{'선택 해제'}</button>}
                    {disabledCount > 0 && <span style={{ color: '#94a3b8' }}>{`고를 수 없는 환자 ${disabledCount}명(오른쪽 사유)`}</span>}
                </div>
                {limitMsg && <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>{limitMsg}</div>}
                {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{error}</div>}
                <div style={{ overflow: 'auto', flex: 1, border: '1px solid #f1f5f9', borderRadius: 8 }}>
                    {loading ? <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13 }}>불러오는 중...</div>
                        : filtered.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>{rows.length === 0 ? '등록된 환자가 없습니다.' : '검색 결과가 없습니다.'}</div>
                            : filtered.map(r => {
                                const st = statusMap.get(r.id) || { selectable: true, text: '' };
                                const checked = selected.has(r.id);
                                const dup = sameNameCountOf(nameCounts, r.name) >= 2;
                                return (
                                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderBottom: '1px solid #f1f5f9', cursor: st.selectable ? 'pointer' : 'not-allowed', background: checked ? '#eef2ff' : 'transparent', opacity: st.selectable ? 1 : 0.75 }}>
                                        <input type={single ? 'radio' : 'checkbox'} checked={checked} disabled={!st.selectable} onChange={() => toggle(r)}
                                            aria-label={`${r.name || '(이름 없음)'} 선택`} />
                                        <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0, flex: 1 }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{r.name || '(이름 없음)'}</span>
                                            {isAppRegisteredRecord(r) && <span style={{ ...U.badge, background: '#ecfeff', color: '#0e7490', fontSize: 12 }}>{appRegisterBadgeLabel(r)}</span>}
                                            {dup && <span style={{ ...U.badge, background: '#ffedd5', color: '#9a3412', fontSize: 12 }}>동명이인 주의</span>}
                                            <span style={{ fontSize: 12, color: '#64748b' }}>
                                                {r.birthDate ? `${r.birthDate} · ` : (r.birthYear ? `${r.birthYear}년 · ` : '')}
                                                {r.phoneLast4 ? `***-${r.phoneLast4}` : '번호 없음'}
                                            </span>
                                        </span>
                                        {st.text && <span style={{ fontSize: 12, color: st.selectable ? '#475569' : '#94a3b8', whiteSpace: 'nowrap' }}>{st.text}</span>}
                                    </label>
                                );
                            })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button type="button" onClick={onClose} style={U.buttonSecondary}>취소</button>
                    <button type="button" onClick={confirm} disabled={selected.size === 0} style={{ ...U.buttonPrimary, opacity: selected.size === 0 ? 0.6 : 1 }}>{`${selected.size}명 고르기`}</button>
                </div>
            </div>
        </div>
    );
}

export default ClinicPatientMultiPicker;
